import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileXls } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { triggerFileDownload } from "@/lib/apiClient";

/**
 * AbstractExportButton — extracted from RABilling.jsx (iter46 refactor).
 *
 * Renders a "ABSTRACT Excel" button that opens a small dialog:
 *   • Overview copy describing the client's ABSTRACT format
 *   • Optional "Override milestone % for this download only" toggle
 *   • 3 inputs (Supply / Install / T&C) with live Σ validator
 *   • Download button (disabled when Σ ≠ 100)
 *
 * Props:
 *   • boqId — id of the BOQ to export
 *   • boq   — full BOQ doc (used for default milestones + filename)
 */
export default function AbstractExportButton({ boqId, boq }) {
  const [open, setOpen] = useState(false);
  const [supply, setSupply] = useState(boq.default_supply_pct ?? 85);
  const [install, setInstall] = useState(boq.default_install_pct ?? 10);
  const [tnc, setTnc] = useState(boq.default_tnc_pct ?? 5);
  const [override, setOverride] = useState(false);

  // Re-sync dialog defaults when the underlying BOQ header %s change while the dialog is closed.
  useEffect(() => {
    if (!open) {
      setSupply(boq.default_supply_pct ?? 85);
      setInstall(boq.default_install_pct ?? 10);
      setTnc(boq.default_tnc_pct ?? 5);
    }
  }, [boq.default_supply_pct, boq.default_install_pct, boq.default_tnc_pct, open]);

  const sum = Number(supply) + Number(install) + Number(tnc);
  const ok = Math.abs(sum - 100) < 0.01;

  const download = () => {
    let url = `/boqs/${boqId}/excel-export`;
    if (override) {
      const qs = new URLSearchParams({ supply_pct: supply, install_pct: install, tnc_pct: tnc }).toString();
      url += `?${qs}`;
    }
    triggerFileDownload(url, `BOQ-${boq.boq_no || boqId}-ABSTRACT.xlsx`);
    setOpen(false);
    toast.success(`ABSTRACT format downloaded${override ? ` · ${supply}/${install}/${tnc}%` : " · using stored milestones"}`);
  };

  return (
    <>
      <Button
        variant="outline"
        className="rounded-sm border-[#0F172A] text-[#0F172A] hover:bg-slate-50"
        onClick={() => setOpen(true)}
        data-testid="boq-download-client-format"
        title="Download this BOQ in the client's ABSTRACT format"
      >
        <FileXls size={13} className="mr-1" /> ABSTRACT Excel
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Download ABSTRACT Format</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-[12.5px] text-slate-600 leading-relaxed">
              Generates the BOQ as an <b>ABSTRACT-format Excel</b> exactly matching the client's original layout:
              header block · two-tier column headers · sub-heads · main-item rows with Supply/Install/T&C milestone breakdowns · Cumulative/Previous/This-bill tracking · Grand Total · signature block.
            </div>
            <label className="flex items-center gap-2 text-[12.5px]">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="abstract-override-toggle" />
              <span>Override milestone % for this download only</span>
            </label>
            {override && (
              <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                <div>
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Supply %</label>
                  <Input type="number" value={supply} onChange={(e) => setSupply(Number(e.target.value) || 0)} className="font-mono-num text-right h-9" data-testid="abstract-supply-override" />
                </div>
                <div>
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Install %</label>
                  <Input type="number" value={install} onChange={(e) => setInstall(Number(e.target.value) || 0)} className="font-mono-num text-right h-9" data-testid="abstract-install-override" />
                </div>
                <div>
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">T&C %</label>
                  <Input type="number" value={tnc} onChange={(e) => setTnc(Number(e.target.value) || 0)} className="font-mono-num text-right h-9" data-testid="abstract-tnc-override" />
                </div>
                <div className="col-span-3">
                  <span className={`text-[11px] font-mono px-2 py-1 rounded-sm ${ok ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-red-100 text-red-800 border border-red-200"}`}>
                    Σ = {sum.toFixed(1)}% {ok ? "✓" : "must be 100"}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              className="rounded-sm bg-[#0F172A] text-white hover:bg-slate-800"
              onClick={download}
              disabled={override && !ok}
              data-testid="abstract-download-confirm"
            >
              <FileXls size={13} className="mr-1" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
